import { useState } from "react";
import { signOut } from "../../app/auth.js";
import { navigate } from "../../app/navigation.js";
import { cleanWorkspaceName } from "../../app/workspace.js";
import { api, type WorkspaceBadge } from "../api.js";
import { Dialog, ErrorBanner, Menu, type MenuItem } from "../common.js";
import { useApp } from "../context.js";
import { t } from "../i18n.js";
import { Icon } from "../Icon.js";
import { NetworkLogo } from "../NetworkLogo.js";
import { NETWORKS } from "../networks.js";
import { badgeCircles, initialOf, tintOf } from "./badge.js";

const TINTS = ["#2F5FD0", "#15803D", "#C2410C", "#7C3AED", "#BE185D", "#0E7490"];

/**
 * L'insigne de l'espace : un cercle par compte social (sa photo, sinon le logo du réseau) puis l'icône du site,
 * qui se chevauchent ; sans aucune photo, l'initiale devant. Une image qui ne charge pas retombe sur le logo, puis l'initiale.
 */
function WorkspaceBadgeView({ name, badge }: { name: string; badge?: WorkspaceBadge }) {
  const [broken, setBroken] = useState<string[]>([]);
  const circles = badgeCircles(badge);
  const initial = (
    <span className="ws-initial" style={{ background: TINTS[tintOf(name, TINTS.length)] }}>
      {initialOf(name)}
    </span>
  );
  const fallback = (platform?: string) => (platform && NETWORKS.includes(platform) ? <NetworkLogo platform={platform} /> : initial);
  return (
    <span className="ws-badge" aria-hidden="true">
      {circles.map((c, i) => (
        <span className={`ws-circle${c.site ? " site" : ""}`} key={c.key} style={{ zIndex: circles.length - i }}>
          {c.src && !broken.includes(c.src) ? (
            <img src={c.src} alt="" referrerPolicy="no-referrer" onError={() => setBroken((b) => [...b, c.src!])} />
          ) : c.site || c.initial ? (
            initial
          ) : (
            fallback(c.platform)
          )}
        </span>
      ))}
    </span>
  );
}

/**
 * Le sélecteur d'espace (un compte, un espace par marque) : la liste des espaces avec une coche
 * sur le courant, « Nouvel espace… », « Renommer cet espace… » (admin), puis le compte. Le même
 * menu en haut à droite sur téléphone et en bas du rail sur ordinateur.
 */
export function WorkspaceMenu({ place }: { place: "top" | "rail" }) {
  const { session, me, workspaceId, workspaces } = useApp();
  const [naming, setNaming] = useState<null | "create" | "rename">(null);
  const list = me?.workspaces ?? [];
  const current = list.find((w) => w.id === workspaceId);
  const name = current?.name ?? t("workspace.fallback");
  const items: MenuItem[] = [
    ...list.map((w) => ({
      label: w.name,
      checked: w.id === workspaceId,
      onSelect: () => workspaces?.choose(w.id),
      testId: `workspace-${w.id}`,
    })),
    ...(me && workspaces
      ? [{ label: t("workspace.new"), icon: "plus" as const, onSelect: () => setNaming("create"), testId: "workspace-new" }]
      : []),
    ...(current?.role === "admin" && workspaces
      ? [{ label: t("workspace.rename"), icon: "pencil" as const, onSelect: () => setNaming("rename"), testId: "workspace-rename" }]
      : []),
    {
      label: t("workspace.account"),
      icon: "user",
      onSelect: () => navigate("/moi"),
      testId: place === "rail" ? "rail-settings" : "tab-me",
    },
    ...(place === "rail"
      ? [
          { label: t("nav.library"), icon: "library" as const, onSelect: () => navigate("/bibliotheque"), testId: "rail-library" },
          { label: t("privacy.link"), icon: "info" as const, onSelect: () => navigate("/confidentialite") },
          { label: t("common.signOut"), icon: "arrowRight" as const, onSelect: () => void signOut() },
        ]
      : []),
  ];
  return (
    <>
      <Menu
        label={t("workspace.label", { name })}
        className={place === "top" ? "only-mobile workspace-top" : ""}
        triggerClass={place === "top" ? "workspace-pill" : "rail-account"}
        header={place === "rail" ? (session.email ?? (session.dev ? "Dev" : undefined)) : t("workspace.title")}
        trigger={
          <>
            <WorkspaceBadgeView key={current?.id} name={name} badge={current?.badge} />
            <span className={`ellipsis ${place === "rail" ? "rail-me" : "workspace-name"}`} data-testid="workspace-current">
              {name}
            </span>
            <Icon name="down" size="sm" />
          </>
        }
        items={items}
      />
      {naming && (
        <WorkspaceNameDialog
          mode={naming}
          id={current?.id}
          initial={naming === "rename" ? (current?.name ?? "") : ""}
          onClose={() => setNaming(null)}
        />
      )}
    </>
  );
}

/** Nommer un nouvel espace, ou renommer l'espace courant. */
function WorkspaceNameDialog({
  mode,
  id,
  initial,
  onClose,
}: {
  mode: "create" | "rename";
  id?: string;
  initial: string;
  onClose: () => void;
}) {
  const { workspaces } = useApp();
  const [value, setValue] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const clean = cleanWorkspaceName(value);
  const save = async () => {
    if (!clean || busy) return;
    setBusy(true);
    try {
      if (mode === "create") workspaces?.upsert(await api.createWorkspace(clean), true);
      else if (id) workspaces?.upsert(await api.renameWorkspace(id, clean));
      onClose();
    } catch (e) {
      setError(e);
      setBusy(false);
    }
  };
  return (
    <Dialog
      title={t(mode === "create" ? "workspace.new.title" : "workspace.rename.title")}
      onClose={onClose}
      actions={
        <>
          <button className="btn quiet" onClick={onClose}>
            {t("common.cancel")}
          </button>
          <button className="btn primary" onClick={() => void save()} disabled={busy || !clean} data-testid="workspace-save">
            {t(mode === "create" ? "workspace.create" : "common.save")}
          </button>
        </>
      }
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void save();
        }}
        className="stack"
      >
        {mode === "create" && <p className="muted">{t("workspace.new.hint")}</p>}
        <input
          className="input"
          value={value}
          maxLength={60}
          autoFocus
          onChange={(e) => setValue(e.target.value)}
          placeholder={t("workspace.placeholder")}
          data-testid="workspace-name-input"
        />
        <ErrorBanner error={error} />
      </form>
    </Dialog>
  );
}


/** En haut à droite (téléphone) : le sélecteur d'espace, sur l'accueil et les Idées. Sur ordinateur, le rail l'a déjà. */

export function AccountLink() {
  return <WorkspaceMenu place="top" />;
}
